import API from '../../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Card, Button } from 'react-bootstrap';

import { Link } from 'react-router-dom';

/* The endpoint returns everything at stock <= 5, so this is the ceiling the
   stock meter is drawn against. */
const LOW_STOCK_THRESHOLD = 5;

/*
  Status colours, not series colours — each ships with an icon and a written
  label so severity never rests on hue alone.
*/
const LEVELS = {
  out: {
    key: 'out',
    color: '#d03b3b',
    soft: '#fef2f2',
    border: '#fecaca',
    icon: '⛔',
    label: 'Out of stock'
  },
  critical: {
    key: 'critical',
    color: '#ec835a',
    soft: '#fff7ed',
    border: '#fed7aa',
    icon: '🔥',
    label: 'Critical'
  },
  low: {
    key: 'low',
    color: '#fab219',
    soft: '#fffbeb',
    border: '#fde68a',
    icon: '⚠',
    label: 'Running low'
  }
};

const levelFor = (stock) => {
  if (!stock || stock <= 0) return LEVELS.out;

  if (stock <= 2) return LEVELS.critical;

  return LEVELS.low;
};

/* Neutral fallback when a product has no image or the URL is dead */
const PLACEHOLDER_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52">
      <rect width="52" height="52" rx="12" fill="#f3ede1"/>
      <text x="26" y="34" font-size="24" text-anchor="middle" fill="#c2410c">📦</text>
    </svg>`
  );

/* Products running low on stock. */
function LowStockCard() {
  const [lowStockProducts, setLowStockProducts] = useState([]);

  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchLowStock = async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/low-stock`
        );
        setLowStockProducts(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchLowStock();
  }, []);

  /* Most urgent first — zero stock is losing sales right now */
  const sorted = [...lowStockProducts].sort(
    (a, b) => (a.stock || 0) - (b.stock || 0)
  );

  const counts = {
    out: sorted.filter((p) => levelFor(p.stock).key === 'out').length,
    critical: sorted.filter((p) => levelFor(p.stock).key === 'critical').length,
    low: sorted.filter((p) => levelFor(p.stock).key === 'low').length
  };

  const visible =
    filter === 'all'
      ? sorted
      : sorted.filter((p) => levelFor(p.stock).key === filter);

  /* Money sitting on the shelf across the flagged products */
  const valueAtRisk = sorted.reduce(
    (sum, p) => sum + (p.price || 0) * (p.stock || 0),
    0
  );

  const chips = [
    { key: 'all', label: 'All', count: sorted.length, color: '#78716c' },
    {
      key: 'out',
      label: 'Out of stock',
      count: counts.out,
      color: LEVELS.out.color
    },
    {
      key: 'critical',
      label: 'Critical',
      count: counts.critical,
      color: LEVELS.critical.color
    },
    {
      key: 'low',
      label: 'Running low',
      count: counts.low,
      color: LEVELS.low.color
    }
  ];

  return (
    <Card className="border-0 mb-5 ls-card">
      <style>{lowStockStyles}</style>

      <Card.Body className="p-3 p-md-4">
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <span className="ls-icon">📉</span>

            <div>
              <h5 className="ls-title">Low Stock Alerts</h5>
              <small className="ls-hint">
                Products at or below {LOW_STOCK_THRESHOLD} units
              </small>
            </div>
          </div>

          {!loading && sorted.length > 0 && (
            <div className="text-end">
              <div className="ls-summary-value">
                ₹{valueAtRisk.toLocaleString('en-IN')}
              </div>
              <div className="ls-summary-label">Stock value remaining</div>
            </div>
          )}
        </div>

        <div className="ls-rule" />

        {loading ? (
          <div className="d-flex flex-column gap-2 pt-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div className="ls-skel-row" key={i}>
                <span className="ls-skel ls-skel-thumb" />
                <div className="flex-grow-1">
                  <span className="ls-skel ls-skel-line" />
                  <span className="ls-skel ls-skel-line short" />
                </div>
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="ls-empty">
            <div className="ls-empty-icon">✅</div>
            <div className="fw-bold ls-empty-title">All Stocked Up</div>
            <small className="text-muted">
              No product is below {LOW_STOCK_THRESHOLD} units. Nothing needs
              restocking right now.
            </small>
          </div>
        ) : (
          <>
            {/* Severity filters */}
            <div className="d-flex flex-wrap gap-2 mb-3">
              {chips
                .filter((chip) => chip.key === 'all' || chip.count > 0)
                .map((chip) => (
                  <button
                    type="button"
                    key={chip.key}
                    className={`ls-chip${filter === chip.key ? ' is-on' : ''}`}
                    style={{ '--chip': chip.color }}
                    onClick={() => setFilter(chip.key)}
                  >
                    {chip.label}
                    <span className="ls-chip-count">{chip.count}</span>
                  </button>
                ))}
            </div>

            <div className="ls-list">
              {visible.map((product) => {
                const level = levelFor(product.stock);

                const pct = Math.min(
                  100,
                  ((product.stock || 0) / LOW_STOCK_THRESHOLD) * 100
                );

                return (
                  <div
                    className="ls-row"
                    key={product._id}
                    style={{
                      '--level': level.color,
                      '--level-soft': level.soft,
                      '--level-border': level.border
                    }}
                  >
                    <span className="ls-row-accent" />

                    <img
                      src={product.image || PLACEHOLDER_IMG}
                      alt={product.name}
                      className="ls-thumb"
                      onError={(e) => {
                        e.currentTarget.src = PLACEHOLDER_IMG;
                      }}
                    />

                    <div className="ls-info">
                      <div className="ls-name" title={product.name}>
                        {product.name}
                      </div>

                      <div className="ls-meta">
                        <span className="ls-badge">
                          {level.icon} {level.label}
                        </span>

                        {product.category && (
                          <span className="ls-cat">{product.category}</span>
                        )}

                        <span className="ls-price">
                          ₹{Number(product.price || 0).toLocaleString('en-IN')}
                        </span>
                      </div>

                      {/* Stock meter — the number is always written out too */}
                      <div className="ls-meter" title={`${product.stock} left`}>
                        <span
                          className="ls-meter-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>

                    <div className="ls-actions">
                      <div className="ls-count">
                        <span className="ls-count-num">{product.stock ?? 0}</span>
                        <span className="ls-count-label">left</span>
                      </div>

                      <Button
                        as={Link}
                        to={`/admin/edit-product/${product._id}`}
                        size="sm"
                        className="ls-restock"
                      >
                        Restock
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
              <small className="text-muted">
                Showing {visible.length} of {sorted.length} flagged products
              </small>

              <Link to="/admin/products" className="ls-all">
                Manage all products →
              </Link>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

const lowStockStyles = `
  .ls-card {
    border-radius: 20px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(124, 45, 18, 0.08);
    transition: box-shadow 0.25s ease;
  }
  .ls-card:hover {
    box-shadow: 0 16px 34px rgba(124, 45, 18, 0.13);
  }

  /* Header */
  .ls-icon {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    border-radius: 14px;
    background: linear-gradient(135deg, #fef2f2, #ffe4e6);
    border: 1px solid #fecaca;
  }
  .ls-title {
    font-weight: 800;
    margin: 0;
    font-size: 1.05rem;
    color: #7c2d12;
  }
  .ls-hint {
    color: #a8a29e;
    font-size: 0.76rem;
  }
  .ls-summary-value {
    font-weight: 800;
    font-size: 1.05rem;
    color: #0b0b0b;
    line-height: 1.2;
  }
  .ls-summary-label {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #a8a29e;
  }
  .ls-rule {
    height: 1px;
    background: linear-gradient(90deg, #fecaca, transparent);
    margin: 14px 0;
  }

  /* Severity filter chips */
  .ls-chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border: 1px solid #ece7df;
    background: #fff;
    color: #78716c;
    font-size: 0.76rem;
    font-weight: 700;
    border-radius: 30px;
    padding: 6px 8px 6px 14px;
    transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;
  }
  .ls-chip:hover {
    border-color: var(--chip);
    color: var(--chip);
  }
  .ls-chip.is-on {
    border-color: var(--chip);
    color: #fff;
    background: var(--chip);
  }
  .ls-chip-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 20px;
    padding: 0 6px;
    border-radius: 30px;
    font-size: 0.7rem;
    font-weight: 800;
    background: rgba(15, 23, 42, 0.07);
    color: inherit;
  }
  .ls-chip.is-on .ls-chip-count {
    background: rgba(255, 255, 255, 0.28);
  }

  /* Rows */
  .ls-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 420px;
    overflow-y: auto;
    padding-right: 4px;
  }
  .ls-list::-webkit-scrollbar { width: 6px; }
  .ls-list::-webkit-scrollbar-thumb {
    background: #ece7df;
    border-radius: 6px;
  }

  .ls-row {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px 10px 16px;
    border-radius: 14px;
    background: var(--level-soft);
    border: 1px solid var(--level-border);
    overflow: hidden;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .ls-row:hover {
    transform: translateX(3px);
    box-shadow: 0 8px 20px rgba(124, 45, 18, 0.12);
  }
  .ls-row-accent {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: var(--level);
  }

  .ls-thumb {
    width: 52px;
    height: 52px;
    flex-shrink: 0;
    object-fit: cover;
    border-radius: 12px;
    background: #fff;
    border: 1px solid rgba(15, 23, 42, 0.07);
  }

  .ls-info {
    flex: 1 1 auto;
    min-width: 0;
  }
  .ls-name {
    font-weight: 700;
    font-size: 0.92rem;
    color: #1c1917;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ls-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin: 4px 0 6px;
  }
  .ls-badge {
    font-size: 0.66rem;
    font-weight: 800;
    color: #fff;
    background: var(--level);
    border-radius: 20px;
    padding: 2px 9px;
    white-space: nowrap;
  }
  .ls-cat, .ls-price {
    font-size: 0.72rem;
    color: #78716c;
    font-weight: 600;
  }
  .ls-price { font-variant-numeric: tabular-nums; }

  /* Stock meter */
  .ls-meter {
    height: 6px;
    border-radius: 6px;
    background: rgba(15, 23, 42, 0.09);
    overflow: hidden;
    max-width: 260px;
  }
  .ls-meter-fill {
    display: block;
    height: 100%;
    border-radius: 6px;
    background: var(--level);
    transition: width 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  }

  /* Right-hand count + action */
  .ls-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
  .ls-count {
    text-align: center;
    min-width: 42px;
  }
  .ls-count-num {
    display: block;
    font-weight: 800;
    font-size: 1.35rem;
    line-height: 1;
    color: var(--level);
  }
  .ls-count-label {
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #a8a29e;
  }
  .ls-restock {
    border: none;
    border-radius: 10px;
    font-weight: 700;
    font-size: 0.78rem;
    padding: 7px 14px;
    white-space: nowrap;
    background: linear-gradient(120deg, #7c2d12, #ea580c);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .ls-restock:hover, .ls-restock:focus, .ls-restock:active {
    background: linear-gradient(120deg, #7c2d12, #ea580c) !important;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(234, 88, 12, 0.35);
  }

  .ls-all {
    font-size: 0.8rem;
    font-weight: 700;
    color: #ea580c;
    text-decoration: none;
  }
  .ls-all:hover { text-decoration: underline; color: #c2410c; }

  /* Empty state */
  .ls-empty {
    text-align: center;
    padding: 40px 16px;
  }
  .ls-empty-icon {
    font-size: 2.8rem;
    margin-bottom: 6px;
  }
  .ls-empty-title {
    color: #15803d;
    font-size: 1.05rem;
  }

  /* Loading */
  .ls-skel-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 14px;
    background: #fdfcfa;
    border: 1px solid #f3efe8;
  }
  .ls-skel {
    display: block;
    background: linear-gradient(90deg, #f1ece4 25%, #faf7f2 37%, #f1ece4 63%);
    background-size: 400% 100%;
    animation: lsShimmer 1.4s ease infinite;
    border-radius: 8px;
  }
  .ls-skel-thumb { width: 52px; height: 52px; border-radius: 12px; flex-shrink: 0; }
  .ls-skel-line { height: 11px; width: 70%; margin-bottom: 8px; }
  .ls-skel-line.short { width: 40%; margin-bottom: 0; }
  @keyframes lsShimmer {
    0%   { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  @media (max-width: 575.98px) {
    .ls-icon { width: 36px; height: 36px; font-size: 1rem; }
    .ls-title { font-size: 0.95rem; }
    .ls-row { flex-wrap: wrap; }
    .ls-thumb { width: 44px; height: 44px; }
    .ls-actions { width: 100%; justify-content: space-between; }
    .ls-meter { max-width: none; }
  }

  @media (prefers-reduced-motion: reduce) {
    .ls-card, .ls-row, .ls-restock, .ls-meter-fill, .ls-skel, .ls-chip {
      transition: none;
      animation: none;
    }
  }
`;

export default LowStockCard;
